import React, { useContext, useRef, useState } from 'react';

import { InputBase, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { useHistory, useLocation } from 'react-router-dom';

import CloseIcon from '../../assets/icons/CloseIcon';
import SearchIcon from '../../assets/icons/SearchIcon';
import { UserInfoCtx } from '../../containers/CommonLayout/CommonLayout';
import styled from '../../styled';
import { colorsPalette, SUBMIDDLE_MARGIN_PX } from '../../theme';
import { useEventListener } from '../../utils/hooks';
import useOverflowHidden from '../../utils/hooks/useOverflowHidden';
import useSearchClient from '../../utils/hooks/useSearchClient';
import Dropdown from './Dropdown';
import useStyles from './styles';

const SearchInput = ({ wrapperClass, isOpen = false, toggle }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [value, setValue] = useState('');
  const { userLoading } = useContext(UserInfoCtx);
  const selectedCategoriesRef = useRef([]);
  useOverflowHidden(isOpen);
  const { search: currentSearchParams } = useLocation();

  const { searchClient, indexName, loadingApiKey } = useSearchClient('autocomplete');

  const onChange = (e) => {
    setValue(e.target.value);
  };

  const search = () => {
    if (!isOpen) return;
    if (!value) return;
    toggle();

    const params = new URLSearchParams(currentSearchParams);
    params.delete('hierarchicalMenu[categories.lvl0]');
    params.set('s', value);
    selectedCategoriesRef.current?.forEach((p) =>
      params.append('hierarchicalMenu[categories.lvl0]', p),
    );

    history.push(`/discover/search/?${params.toString()}`);
  };

  useEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      search();
    }
  });

  useEventListener('keyup', (e) => {
    if (e.key === 'Escape' && isOpen) {
      toggle();
    }
  });

  const resetSearch = () => setValue('');

  const validValue = value?.length >= 2;

  return (
    <div
      onClick={!isOpen ? toggle : () => {}}
      className={cx(classes.wrapper, isOpen && classes.wrapperOpen, wrapperClass)}
    >
      {isOpen && (
        <CloseSearch
          show={isOpen && !value}
          data-testid="closeButton"
          aria-label="close"
          onClick={toggle}
          size="large"
        >
          <StyledCloseIcon />
        </CloseSearch>
      )}
      {isOpen && (
        <ResetSearch
          show={isOpen && value}
          data-testid="clearSearchBox"
          aria-label="clear"
          onClick={() => {
            resetSearch();
          }}
          size="large"
        >
          <StyledCloseIcon />
        </ResetSearch>
      )}
      <SearchWrapper>
        <div className={classes.inputWrapper}>
          {isOpen && (
            <StyledInput
              isOpen={isOpen}
              value={value}
              onChange={onChange}
              placeholder={t('input.placeholder.search')}
              autoFocus={isOpen}
              inputProps={{ 'aria-label': 'enter key words', 'data-testid': 'searchField' }}
            />
          )}
          {!isOpen && (
            <Typography data-testid="placeholder" className={classes.searchPlaceholder}>
              {t('input.placeholder.search')}
            </Typography>
          )}
          <IconButton
            data-testid="submitSearch"
            type="submit"
            aria-label="search"
            onClick={isOpen ? search : toggle}
            size="large"
          >
            <SearchIcon className={classes.searchIcon} />
          </IconButton>
        </div>
        <InstantSearch searchClient={searchClient} indexName={indexName}>
          <Configure hitsPerPage={10} distinct={3} clickAnalytics />
          {isOpen && validValue && (
            <Dropdown
              selectedCategoriesRef={selectedCategoriesRef}
              value={value}
              close={toggle}
              canCheckAlgoliaToken={Boolean(
                !loadingApiKey && process.env.REACT_APP_ALGOLIA_APP_NAME && !userLoading,
              )}
            />
          )}
        </InstantSearch>
      </SearchWrapper>
    </div>
  );
};

export default SearchInput;

const StyledInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: relative;
  top: -2px;
  padding-left: ${({ value }) => value && SUBMIDDLE_MARGIN_PX};
  flex-grow: 1;
  transition: 0.3s padding-left;
  font-size: 20px;

  & input::placeholder {
    font-size: 20px;
    color: ${alpha(colorsPalette.white, 0.33)};
  }
`;
const SearchWrapper = styled('div')`
  width: 100%;
`;
const ResetSearch = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  left: 4px;
  top: 50%;
  z-index: 1;
  transform: translateY(-50%) scale(${({ show }) => (show ? 1 : 0.8)});
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.3s;
  transition-delay: ${({ show }) => (show ? '0.2s' : 0)};
`;
const CloseSearch = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  left: ${({ show }) => (show ? '-50px' : '-40px')};
  top: 50%;
  transform: translateY(-50%) scale(${({ show }) => (show ? 1 : 0.8)});

  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.3s;
  transition-delay: ${({ show }) => (show ? '0.2s' : 0)};
`;
const StyledCloseIcon = styled(CloseIcon)`
  min-width: 18px;
  width: 18px;
  height: 18px;
`;
